import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ConnectionRight } from '@shared/interfaces/nts/connection-right';

const danishDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Copenhagen',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export type ConnectionRightForm = {
  maximumCurrent: FormControl<number | null>;
  maximumPower: FormControl<number | null>;
  limitedGridAccess: FormControl<number | null>;
  limitedGridAccessCurrent: FormControl<number | null>;
  comment: FormControl<string | null>;
  validFrom: FormControl<string | null>;
};

export function createConnectionRightForm(connectionRight?: ConnectionRight): FormGroup<ConnectionRightForm> {
  let existingValidFrom: string | undefined;
  if (connectionRight?.validFrom) {
    existingValidFrom = /^\d{4}-\d{2}-\d{2}$/.test(connectionRight.validFrom)
      ? connectionRight.validFrom
      : getDanishDate(new Date(connectionRight.validFrom));
  }

  return new FormGroup<ConnectionRightForm>({
    maximumCurrent: new FormControl(connectionRight?.maximumCurrent ?? null, [Validators.min(0), Validators.max(999999)]),
    maximumPower: new FormControl(connectionRight?.maximumPower ?? null, [Validators.min(0), Validators.max(999999)]),
    limitedGridAccess: new FormControl(connectionRight?.limitedGridAccess ?? null, [Validators.min(0), Validators.max(999999)]),
    limitedGridAccessCurrent: new FormControl(connectionRight?.limitedGridAccessCurrent ?? null, [Validators.min(0), Validators.max(999999)]),
    comment: new FormControl(connectionRight?.comment ?? null, Validators.maxLength(500)),
    validFrom: new FormControl(
      getDanishDate(),
      [
        Validators.required,
        validDateValidator,
        validFromNotInFutureValidator,
        control => validFromNotBeforeExistingValidator(control, existingValidFrom),
      ],
    ),
  });
}

export function validDateValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const value = control.value as string;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return { invalidDate: true };
  }

  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? null
    : { invalidDate: true };
}

export function validFromNotInFutureValidator(
  control: AbstractControl,
  maximumValidFrom = getDanishDate(),
): ValidationErrors | null {
  if (!control.value || validDateValidator(control)) {
    return null;
  }

  return (control.value as string) > maximumValidFrom ? { futureDate: true } : null;
}

export function getDanishDate(date = new Date()): string {
  const parts = danishDateFormatter.formatToParts(date);
  const year = parts.find(part => part.type === 'year')?.value;
  const month = parts.find(part => part.type === 'month')?.value;
  const day = parts.find(part => part.type === 'day')?.value;

  if (!year || !month || !day) {
    throw new Error('Could not format Danish calendar date.');
  }

  return `${year}-${month}-${day}`;
}

export function validFromNotBeforeExistingValidator(
  control: AbstractControl,
  existingValidFrom?: string | null,
): ValidationErrors | null {
  if (!existingValidFrom || !control.value || validDateValidator(control)) {
    return null;
  }

  return (control.value as string) < existingValidFrom ? { earlierDate: true } : null;
}
